import type { Invoice } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { useTranslate } from "@/lib/i18n";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { useErrorMessage } from "@/shared/errors";
import { useLocalName } from "@/shared/localName";
import { useMoney } from "@/shared/ui";
import { groupPlates } from "@/modules/pos/lib/groupPlates";

/** The plates a waiting bag carries to the customer: fired, charged, not voided. */
const FIRED = new Set(["QUEUED", "PREPARING", "READY", "SERVED"]);

/**
 * One waiting bag's lines, under its row (B6 · P24, plan D-37): `qty×`, the
 * dish, its note / add-ons, and the line's price.
 *
 * Read ONLY when the row is expanded — the single-bill read the register uses
 * (`GET restaurant/invoices/:id`, the same query key, so opening the bill
 * afterwards is instant) — never prefetched for the whole table. Grouped with
 * the register's own `groupPlates`, so three teas read as one "3×" line.
 */
export function WaitingOrderLines({ invoiceId, testId }: { invoiceId: string; testId: string }) {
  const t = useTranslate();
  const money = useMoney();
  const localName = useLocalName();
  const errorMessage = useErrorMessage();
  const invoice = useApiQuery<Invoice>(queryKeys.invoice(invoiceId), endpoints.restaurant.invoice(invoiceId), {
    staleTime: 15_000,
  });

  const groups = invoice.data
    ? groupPlates(
        invoice.data.items.filter(
          (item) => item.billingState === "CHARGED" && FIRED.has(item.fulfillmentState),
        ),
        money,
      )
    : [];

  return (
    <div data-ui="waiting-lines" data-testid={testId} role="cell" className="flex flex-col">
      {invoice.isLoading ? (
        <p data-ui="waiting-lines-note" aria-busy="true">
          {t((m) => m.dispatch.waitingItemsLoading)}
        </p>
      ) : invoice.isError ? (
        <p data-ui="waiting-lines-note" role="alert">
          {t((m) => m.dispatch.waitingItemsFailed, { error: errorMessage(invoice.error) })}
        </p>
      ) : groups.length === 0 ? (
        <p data-ui="waiting-lines-note">{t((m) => m.dispatch.waitingItemsNone)}</p>
      ) : (
        <ul data-ui="waiting-lines-card" className="m-0 flex list-none flex-col p-0">
          {groups.map((group) => {
            const extra = [
              group.addonLabel ? localName({ nameAr: group.addonLabel, nameEn: group.addonLabelEn }) : null,
              group.substitutionLabel
                ? localName({ nameAr: group.substitutionLabel, nameEn: group.substitutionLabelEn })
                : null,
              group.note,
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <li key={group.key} data-ui="waiting-line" className="flex items-baseline">
                <span data-ui="waiting-line-qty" className="tabular-nums">
                  {group.quantity}×
                </span>
                <span className="flex min-w-0 flex-1 flex-wrap" data-ui="waiting-line-text">
                  <span data-ui="waiting-line-name">
                    {localName({ nameAr: group.plateNameAr, nameEn: group.plateNameEn })}
                  </span>
                  {extra ? <span data-ui="waiting-line-note">{extra}</span> : null}
                </span>
                <span data-ui="waiting-line-price" className="tabular-nums">
                  {money(group.total)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
